import { RadixInput, RadixSelect } from '@/components/radix';
import {
    durationOperatorFromUi,
    durationOperatorToUi,
    getDurationOperatorOptions,
    type DurationOperatorUi,
} from '@/data/Marketing/marketingDurationOperators';
import {
    convertDurationToHours,
    convertHoursToDuration,
} from '@/redux/slices/Marketing/triggerFlow/triggerFlowDataUtils';
import { t } from 'i18next';
import React, { useEffect, useMemo } from 'react';

export type DaySelectorLabel = 'HOURS' | 'DAYS' | 'WEEKS' | 'MONTHS';

/** Full duration state: amount (hours), unit (label), and optional time direction (operator). */
export type DaySelectorValue = {
    /** `null` while the amount input is cleared so the field can stay empty. */
    hours: number | null;
    label: DaySelectorLabel;
    operator?: string;
};

export const DEFAULT_DAY_SELECTOR_VALUE: DaySelectorValue = {
    hours: 7,
    label: 'DAYS',
    operator: 'AFTER_HOURS',
};

function getDaySelectorAmountUnit(value: DaySelectorValue): string {
    const count = value.hours;
    if (count === null || count === undefined) {
        return t('Marketing.DaySelectorDurationIncomplete');
    }
    return t(`Marketing.DaySelectorDurationAmount${value.label}`, { count });
}

function resolveSummaryNoteKey(
    baseKey: string | undefined,
    showOperator: boolean,
    operatorUi: DurationOperatorUi,
): string | undefined {
    if (!baseKey) return undefined;
    if (!showOperator) return baseKey;
    return `${baseKey}${operatorUi}`;
}

interface DaySelectorProps {
    value: DaySelectorValue;
    onChange: (value: DaySelectorValue) => void;
    label?: string;
    error?: string;
    showError?: boolean;
    /** When true, shows time-direction dropdown and reads/writes `value.operator`. */
    showOperator?: boolean;
    /** Limits which operators appear (default: all four). */
    operatorOptions?: DurationOperatorUi[];
    /** Limits which duration units appear (default: all four). */
    unitOptions?: DaySelectorLabel[];
    /** Base i18n key; with operators, suffix WITHIN | BEFORE | AFTER is appended. */
    summaryNoteKey?: string;
    summaryParams?: Record<string, string | number>;
}

export default function DaySelector({
    value,
    onChange,
    label,
    error,
    showError,
    showOperator = false,
    operatorOptions = ['WITHIN', 'BEFORE', 'AFTER', 'NEXT'],
    unitOptions = ['HOURS', 'DAYS', 'WEEKS', 'MONTHS'],
    summaryNoteKey,
    summaryParams,
}: DaySelectorProps) {
    const operatorSelectOptions = useMemo(
        () => getDurationOperatorOptions(operatorOptions),
        [operatorOptions],
    );

    const operatorUi = durationOperatorToUi(value.operator);
    const allowedOperatorUi = operatorSelectOptions.map((o) => o.value);

    useEffect(() => {
        if (!showOperator || allowedOperatorUi.length === 0) return;
        if (allowedOperatorUi.includes(operatorUi)) return;
        onChange({
            ...value,
            operator: durationOperatorFromUi(allowedOperatorUi[0]),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOperator, allowedOperatorUi.join(','), operatorUi]);

    // Coerce a disallowed unit (e.g. legacy HOURS) to the first allowed one, converting the amount to keep the duration equivalent
    useEffect(() => {
        if (unitOptions.length === 0 || unitOptions.includes(value.label)) return;
        const nextLabel = unitOptions[0];
        onChange({
            ...value,
            label: nextLabel,
            hours:
                value.hours !== null
                    ? Math.max(1, Math.round(convertHoursToDuration(convertDurationToHours(value.hours, value.label), nextLabel)))
                    : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitOptions.join(','), value.label]);

    const patch = (partial: Partial<DaySelectorValue>) => onChange({ ...value, ...partial });

    const amountUnit = useMemo(() => getDaySelectorAmountUnit(value), [value]);

    const resolvedSummaryKey = resolveSummaryNoteKey(summaryNoteKey, showOperator, operatorUi);
    const summaryNote = resolvedSummaryKey
        ? t(resolvedSummaryKey, { amountUnit, ...summaryParams })
        : null;

    return (
        <div className="w-full">
            {label ? <label className="block text-sm font-medium text-text-primary mb-2">{label}</label> : null}
            <div className="flex flex-row items-center gap-2 w-full">
                {showOperator ? (
                    <RadixSelect
                        options={operatorSelectOptions}
                        value={operatorUi}
                        onValueChange={(next) =>
                            patch({ operator: durationOperatorFromUi(next as DurationOperatorUi) })
                        }
                        placeholder={t('Common.Select')}
                        className="!max-w-fit"
                    />
                ) : null}
                <RadixSelect
                    options={[
                        { label: t('Marketing.DurationHours'), value: 'HOURS' },
                        { label: t('Marketing.DurationDays'), value: 'DAYS' },
                        { label: t('Marketing.DurationWeeks'), value: 'WEEKS' },
                        { label: t('Marketing.DurationMonths'), value: 'MONTHS' },
                    ].filter((option) => unitOptions.includes(option.value as DaySelectorLabel))}
                    value={value.label}
                    onValueChange={(val) => patch({ label: val as DaySelectorLabel })}
                    className={` ${showOperator ? '!max-w-fit' : 'w-full'}`}
                />
                <RadixInput
                    placeholder="0"
                    value={value.hours === null ? '' : value.hours}
                    onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        patch({ hours: raw === '' ? null : Number(raw) });
                    }}
                    className={`${showOperator ? '!max-w-fit' : 'w-full'}`}
                />
            </div>
            {summaryNote ? (
                <p className="text-xs leading-5 font-normal text-text-secondary mt-2 m-0">{summaryNote}</p>
            ) : null}
            {showError && error ? <div className="text-sm text-error-500 mt-1">{error}</div> : null}
        </div>
    );
}
